import { useCallback, useEffect, useRef, useState } from 'react';
import { Select, ListBox, Label, Button } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { RendererController } from '@/renderer/controller';
import { sceneBaseRotation } from '@/pages/explore/utils/sceneRotation';
import { useViewerStore } from '@stores/viewerStore';
import { useAssetLibraryStore } from '@stores/assetLibraryStore';
import { replayModels, syncModelList, syncModelTransform } from '@/modules/asset-library/modelSync';
import { useRaceTrackStore } from '../useRaceTrackStore';
import { raceKeyframesToTrack } from '../keyframeUtils';
import type { ViewerModelHandle, Transform } from '@mindcloudxai_3d/core';

type AspectRatio = '16:9' | '9:16' | '1:1';

const ASPECT_RATIO_MAP: Record<AspectRatio, number> = {
  '16:9': 16 / 9,
  '9:16': 9 / 16,
  '1:1': 1,
};

interface TrackPreviewProps {
  onClose: () => void;
  getController: () => RendererController | null;
}

// 格式化时间为 00:00.00
function formatTime(seconds: number) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 100);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
}

export function TrackPreview({ onClose, getController }: TrackPreviewProps) {
  const { t } = useTranslation();
  const isPlaying = useRaceTrackStore((s) => s.isPlaying);
  const duration = useRaceTrackStore((s) => s.duration);
  const isEditing = useRaceTrackStore((s) => s.isEditing);
  const isPreviewOpen = useRaceTrackStore((s) => s.isPreviewOpen);
  const keyframes = useRaceTrackStore((s) => s.keyframes);

  const sceneConfig = useViewerStore((s) => s.sceneConfig);
  const taskData = useViewerStore((s) => s.taskData);
  const assets = useAssetLibraryStore((s) => s.assets);

  const containerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<RendererController | null>(null);
  const keyframesRef = useRef(keyframes);
  const durationRef = useRef(duration);
  const previewTimeRef = useRef(0);
  // 主视窗模型 ID → 预览 controller 模型 ID 的映射
  const modelIdMapRef = useRef<Map<string, string>>(new Map());
  const pendingModelIdsRef = useRef<Set<string>>(new Set());
  const assetsRef = useRef(assets);
  assetsRef.current = assets;
  const [previewReady, setPreviewReady] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // 预览窗口尺寸与比例
  const [previewWidth, setPreviewWidth] = useState(400);
  const [aspectRatio, setAspectRatio] = useState<string | number | null>('16:9');
  // 拖拽位置：默认左下角，UserPill 右侧、TrackEditor 上方
  const [pos, setPos] = useState({ left: 120, bottom: 200 });

  const dragRef = useRef<{
    startX: number;
    startY: number;
    origLeft: number;
    origBottom: number;
  } | null>(null);

  const handleDragStart = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origLeft: pos.left,
        origBottom: pos.bottom,
      };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [pos],
  );

  const handleDragMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPos({
      left: dragRef.current.origLeft + dx,
      bottom: dragRef.current.origBottom - dy,
    });
  }, []);

  const handleDragEnd = useCallback((e: React.PointerEvent) => {
    dragRef.current = null;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  }, []);

  // resize 拖拽
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const handleResizeStart = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      resizeRef.current = { startX: e.clientX, startWidth: previewWidth };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [previewWidth],
  );
  const handleResizeMove = useCallback((e: React.PointerEvent) => {
    if (!resizeRef.current) return;
    const dx = e.clientX - resizeRef.current.startX;
    setPreviewWidth(Math.max(200, Math.min(resizeRef.current.startWidth + dx, 800)));
  }, []);
  const handleResizeEnd = useCallback((e: React.PointerEvent) => {
    resizeRef.current = null;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  }, []);

  keyframesRef.current = keyframes;
  durationRef.current = duration;

  const hasPreviewTrack = keyframes.length >= 2;
  const hasPreviewTrackRef = useRef(hasPreviewTrack);
  hasPreviewTrackRef.current = hasPreviewTrack;

  const editorTime = useRaceTrackStore((s) => s.editorTime);
  const setEditorTime = useRaceTrackStore((s) => s.setEditorTime);
  // 没有关键帧时不更新 previewTime，避免进度条空转
  const previewTime = isEditing && hasPreviewTrack ? editorTime : 0;
  previewTimeRef.current = previewTime;

  const syncPreviewCameraPoseToMain = useCallback(() => {
    const ctrl = controllerRef.current;
    if (!ctrl) return;
    ctrl.flushFrame(0);
    const pose = ctrl.camera.get();
    getController()?.scene.showPreviewCameraPose?.(pose);
  }, [getController]);

  // 播放/暂停：由 TrackEditor 的 handlePlayToggle 设置 store 状态，
  // TrackPreview 响应 isPlaying 在自己的 controller 上执行播放或暂停
  useEffect(() => {
    if (!isEditing || !previewReady || !hasPreviewTrack) return;
    const ctrl = controllerRef.current;
    if (!ctrl) return;
    if (isPlaying) {
      // 如果已到末尾，seek 回 0 再播放
      const state = ctrl.navigation.anim.getState();
      if (state.time >= duration - 0.01) {
        ctrl.navigation.anim.seek(0);
        setEditorTime(0);
        syncPreviewCameraPoseToMain();
      }
      ctrl.navigation.setMode('anim');
      ctrl.navigation.anim.play();
    } else {
      ctrl.navigation.anim.pause();
    }
  }, [
    isPlaying,
    isEditing,
    previewReady,
    hasPreviewTrack,
    duration,
    setEditorTime,
    syncPreviewCameraPoseToMain,
  ]);

  // 播放中通过 anim 时间推进更新 editorTime，暂停时响应外部 seek
  useEffect(() => {
    if (!isEditing || !hasPreviewTrack) return;
    if (isPlaying) return; // 播放中不 seek，让 anim 自然推进
    controllerRef.current?.navigation.anim.pause();
    controllerRef.current?.navigation.anim.seek(editorTime);
    syncPreviewCameraPoseToMain();
  }, [editorTime, isEditing, hasPreviewTrack, isPlaying, syncPreviewCameraPoseToMain]);

  // 播放中轮询 anim 时间同步到 store，驱动进度条
  // track 使用 loopMode='repeat'，anim 到末尾后自动循环，不在 JS 层停止
  useEffect(() => {
    if (!isPlaying || !previewReady || !hasPreviewTrack) return;
    let raf = 0;
    const tick = () => {
      const ctrl = controllerRef.current;
      if (ctrl) {
        const state = ctrl.navigation.anim.getState();
        setEditorTime(state.time);
        syncPreviewCameraPoseToMain();

        if (state.time >= duration && !state.paused) {
          useRaceTrackStore.getState().setPlaying(false);
          return;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [
    isPlaying,
    previewReady,
    hasPreviewTrack,
    duration,
    setEditorTime,
    syncPreviewCameraPoseToMain,
  ]);

  const disposePreview = useCallback(() => {
    pendingModelIdsRef.current.clear();
    modelIdMapRef.current.clear();
    controllerRef.current?.dispose();
    controllerRef.current = null;
    setPreviewReady(false);
  }, []);

  // 预览 controller 仅在进入/退出编辑时创建/销毁，关闭预览窗口时保留实例避免重新加载 splat
  useEffect(() => {
    if (!isEditing || !sceneConfig || !containerRef.current) {
      disposePreview();
      return;
    }

    let cancelled = false;
    const ctrl = new RendererController();
    const modelIdMap = modelIdMapRef.current;
    const pendingModelIds = pendingModelIdsRef.current;
    controllerRef.current = ctrl;
    setPreviewReady(false);
    setPreviewError(null);

    const initPreview = async () => {
      try {
        // 进入编辑前先读取主窗口相机，确保预览视角与主窗口一致
        const mainCtrl = getController();
        const mainCamera = mainCtrl?.camera.get();

        await ctrl.init({
          container: containerRef.current!,
          background: '#020617',
          renderer: 'playcanvas',
          deviceType: 'webgl',
          avatarEnabled: false,
          avatarFollowCamera: false,
          inputEnabled: false,
          camera: mainCamera,
        });
        if (cancelled) return;

        // 有足够关键帧时才传 animTrack，否则仅加载场景
        const track = hasPreviewTrackRef.current
          ? raceKeyframesToTrack(keyframesRef.current, durationRef.current, 'repeat')
          : undefined;
        await ctrl.loadScene({
          url: sceneConfig.asset.url,
          sogMeta: sceneConfig.asset.sogMeta,
          settings: sceneConfig.experienceSettings,
          animTrack: track,
          autoPlayAnim: false,
          debugTrackRoute: false,
          baseRotation: sceneBaseRotation(taskData ? (taskData.is_yup ?? false) : true),
        });
        if (cancelled) return;

        if (track) {
          ctrl.navigation.anim.setTrack(track);
          ctrl.navigation.setMode('anim');
          ctrl.navigation.anim.pause();
          ctrl.navigation.anim.seek(0);
          ctrl.scene.showAnimTrackRoute?.(null);
          syncPreviewCameraPoseToMain();
        } else if (mainCamera) {
          // loadScene 可能用 scene settings 覆盖相机，需在场景就绪后重新应用
          ctrl.camera.set(mainCamera);
        }

        // 重放主视窗中的模型到预览 controller
        pendingModelIds.clear();
        modelIdMap.clear();
        const replayedModels = await replayModels(ctrl, assetsRef.current);
        if (cancelled) return;
        for (const [mainId, targetId] of replayedModels) {
          modelIdMap.set(mainId, targetId);
        }

        setPreviewReady(true);
      } catch (err) {
        if (!cancelled) {
          setPreviewError(err instanceof Error ? err.message : t('raceTrack.previewFailed'));
        }
      }
    };

    void initPreview();

    return () => {
      cancelled = true;
      pendingModelIds.clear();
      modelIdMap.clear();
      ctrl.dispose();
      if (controllerRef.current === ctrl) controllerRef.current = null;
    };
    // hasPreviewTrack 用 ref 避免关键帧从 <2 变为 >=2 时重建 controller
    // 不依赖 isPreviewOpen：关闭预览窗口时保留 controller 实例
  }, [
    disposePreview,
    getController,
    isEditing,
    sceneConfig,
    taskData,
    syncPreviewCameraPoseToMain,
  ]);

  // 实时同步主视窗模型变化到预览 controller
  useEffect(() => {
    if (!isEditing || !previewReady) return;
    const mainCtrl = getController();
    const previewCtrl = controllerRef.current;
    if (!mainCtrl || !previewCtrl) return;

    const onModelChanged = (models: ViewerModelHandle[]) => {
      syncModelList(
        models,
        previewCtrl,
        modelIdMapRef.current,
        assetsRef.current,
        pendingModelIdsRef.current,
      );
    };
    const onModelTransform = (event: { modelId: string; transform: Transform }) => {
      syncModelTransform(event.modelId, event.transform, previewCtrl, modelIdMapRef.current);
    };

    mainCtrl.on('model:changed', onModelChanged);
    mainCtrl.on('model:transform', onModelTransform);
    return () => {
      mainCtrl.off('model:changed', onModelChanged);
      mainCtrl.off('model:transform', onModelTransform);
    };
  }, [isEditing, previewReady, getController]);

  // asset store 变化时重新同步（setAssetSource 在 model:changed 之后才执行，
  // 首次导入的模型 source 在 model:changed 事件触发时尚未就绪，需要这里补同步）
  useEffect(() => {
    if (!isEditing || !previewReady) return;
    const mainCtrl = getController();
    const previewCtrl = controllerRef.current;
    if (!mainCtrl || !previewCtrl) return;
    const mainModels = mainCtrl.model.getModels();
    syncModelList(
      mainModels,
      previewCtrl,
      modelIdMapRef.current,
      assets,
      pendingModelIdsRef.current,
    );
  }, [isEditing, previewReady, assets, getController]);

  useEffect(() => {
    if (!isEditing || !previewReady || !hasPreviewTrack) return;
    const ctrl = controllerRef.current;
    if (!ctrl) return;
    const track = raceKeyframesToTrack(keyframes, duration, 'repeat');
    ctrl.navigation.anim.setTrack(track);
    ctrl.navigation.setMode('anim');
    // keyframes 变化时重建轨道，保持当前播放头位置而非重置到 0
    ctrl.navigation.anim.pause();
    ctrl.navigation.anim.seek(previewTimeRef.current);
    ctrl.scene.showAnimTrackRoute?.(null);
    syncPreviewCameraPoseToMain();
  }, [duration, hasPreviewTrack, isEditing, keyframes, previewReady, syncPreviewCameraPoseToMain]);
  // 比例/宽度变化或预览窗口重新打开时需手动触发 resize
  useEffect(() => {
    if (!previewReady || !isPreviewOpen) return;
    const ctrl = controllerRef.current;
    const el = containerRef.current;
    if (!ctrl || !el) return;
    const { width, height } = el.getBoundingClientRect();
    if (width > 0 && height > 0) ctrl.resize(width, height);
  }, [aspectRatio, previewWidth, previewReady, isPreviewOpen]);

  if (!isEditing) return null;

  // 关闭预览时隐藏 UI 但保留 controller 实例（container 始终挂载）

  // 根据比例计算 canvas 高度
  const canvasHeight = Math.round(
    previewWidth / ASPECT_RATIO_MAP[(aspectRatio as AspectRatio) ?? '16:9'],
  );

  return (
    <div
      className={`group/window pointer-events-auto absolute bottom-0 left-0 z-40 flex max-h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-black/80 text-white shadow-xl backdrop-blur-sm select-none ${
        isPreviewOpen ? '' : 'pointer-events-none opacity-0'
      }`}
      style={{ left: `${pos.left}px`, bottom: `${pos.bottom}px`, width: `${previewWidth}px` }}
    >
      {/* 标题栏：拖拽手柄 + Preview 标签 + 比例选择 + 关闭 */}
      <div
        className="flex cursor-grab flex-row items-center justify-between gap-2 border-b border-white/10 px-3 py-2 active:cursor-grabbing"
        onPointerDown={handleDragStart}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
      >
        <div className="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-3.5 w-3.5"
            aria-hidden="true"
          >
            <circle cx="9" cy="12" r="1" />
            <circle cx="9" cy="5" r="1" />
            <circle cx="9" cy="19" r="1" />
            <circle cx="15" cy="12" r="1" />
            <circle cx="15" cy="5" r="1" />
            <circle cx="15" cy="19" r="1" />
          </svg>
          <span className="text-xs font-medium select-none">{t('raceTrack.preview')}</span>
          {/* 比例选择下拉 — HeroUI Select */}
          <Select
            value={aspectRatio}
            onChange={(v) => setAspectRatio(v)}
            variant="secondary"
            aria-label={t('raceTrack.aspectRatio')}
            className="ml-1"
          >
            <Label className="sr-only">{t('raceTrack.aspectRatio')}</Label>
            <Select.Trigger className="h-7 w-fit min-w-[64px] rounded-md border border-white/10 bg-white/5 px-2 text-xs text-white hover:bg-white/10">
              <Select.Value />
              <Select.Indicator className="size-3.5 text-white/50" />
            </Select.Trigger>
            <Select.Popover className="min-w-[80px] border border-white/10 bg-black/90 backdrop-blur-sm">
              <ListBox className="py-1">
                <ListBox.Item
                  id="16:9"
                  textValue="16:9"
                  className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                >
                  16:9
                  <ListBox.ItemIndicator />
                </ListBox.Item>
                <ListBox.Item
                  id="9:16"
                  textValue="9:16"
                  className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                >
                  9:16
                  <ListBox.ItemIndicator />
                </ListBox.Item>
                <ListBox.Item
                  id="1:1"
                  textValue="1:1"
                  className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                >
                  1:1
                  <ListBox.ItemIndicator />
                </ListBox.Item>
              </ListBox>
            </Select.Popover>
          </Select>
        </div>
        <Button
          isIconOnly
          variant="ghost"
          size="sm"
          aria-label={t('raceTrack.closePreview')}
          onPress={onClose}
          className="size-6 rounded-md text-white/60 hover:bg-white/10 hover:text-white"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="size-3.5"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </Button>
      </div>

      {/* Canvas 区域 */}
      <div
        className="relative w-full bg-black"
        style={{ aspectRatio: `${previewWidth} / ${canvasHeight}` }}
      >
        <div ref={containerRef} className="absolute inset-0" />
        {!previewReady && !previewError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black font-mono text-[10px] tracking-widest text-white/45">
            {t('raceTrack.loadingPreview')}
          </div>
        )}
        {previewError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black px-4 text-center font-mono text-[10px] tracking-widest text-red-300/80">
            {previewError}
          </div>
        )}
        {/* 3. 底部时间刻度与进度 */}
        <div className="absolute bottom-2 left-2 font-mono text-[10px] font-semibold tracking-widest text-white/90">
          {formatTime(previewTime)}
        </div>

        <div className="absolute right-2 bottom-2 font-mono text-[10px] text-white/40">
          /{formatTime(duration)}
        </div>

        {/* 进度微指示条 */}
        <div className="absolute inset-x-0 bottom-0 h-0.5 bg-white/5">
          <div
            className="h-full bg-indigo-500 shadow-[0_0_4px_#6366f1] transition-all duration-100"
            style={{ width: `${Math.min((previewTime / (duration || 8)) * 100, 100)}%` }}
          />
        </div>
      </div>

      {/* Resize 手柄 */}
      <div
        className="absolute right-0 bottom-0 flex h-6 w-6 cursor-se-resize items-center justify-center opacity-0 transition-opacity group-hover/window:opacity-100"
        onPointerDown={handleResizeStart}
        onPointerMove={handleResizeMove}
        onPointerUp={handleResizeEnd}
      >
        <svg
          aria-label={t('raceTrack.resizeHandle')}
          className="h-5 w-5"
          fill="none"
          role="img"
          viewBox="0 0 20 20"
        >
          <path
            d="M17 3C17 10.732 10.732 17 3 17"
            stroke="white"
            strokeLinecap="round"
            strokeOpacity="0.7"
            strokeWidth="4"
          />
        </svg>
      </div>
    </div>
  );
}
